import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { partnerLogoPfad, restzeit, type GewinnspielPreisStand } from '@k4ia/shared'
import { Avatar } from '../components/Avatar'
import {
  fetchGewinnspielPreise,
  gewinnspielSpeechUrl,
  reportGewinnspielScreen,
  type GewinnspielAnsage,
  type GewinnspielPreis,
  type GewinnspielStand,
} from '../lib/api'
import { neuenStandLaden, neuerStandDeployt } from '../lib/stand'
import { boxenRaster, stilleWav } from './IntroPage'

/** Wie oft der Bildschirm seinen Stand meldet und den der Ziehung holt. */
const POLL_MS = 1000
/** So lange rattern die Namen, bevor ein gezogener steht. */
const TROMMEL_MS = 6000
/** Für eine einzelne Neuziehung reicht eine kürzere Trommel. */
const TROMMEL_NEU_MS = 4000
/** Wie oft der Bildschirm nachsieht, ob ein neuer Stand deployt ist. */
const STAND_PRUEFEN_MS = 60_000
/** Namen, die beim Rattern vorbeiziehen — nicht die echten, die bleiben bis zur Ziehung geheim. */
const TROMMEL_NAMEN = [
  'lea.k',
  'max_m',
  'style.by.sara',
  'tom.s',
  'nina_fashion',
  'julia.l',
  'paul.r',
  'mia.wears',
  'jonas_h',
  'lisa.m',
  'felix.w',
  'hannah.b',
  'noah.k',
  'emma.st',
  'leon.vl',
]

type Phase = 'bereit' | 'wartet' | 'ziehung' | 'fertig'

/**
 * `/gewinnspiel`: der große Bildschirm der Auslosung.
 *
 * 1. **Bereit:** ein Klick gibt den Ton frei und geht in den Vollbildmodus.
 * 2. **Wartet:** K4ia und die Preise mit Logo — „Gleich geht's los".
 * 3. **Ziehung:** Am Handy wird gestartet; die Namen rattern ein paar
 *    Sekunden, dann steht für jeden Preis ein Gewinner mit laufender Frist.
 *    Wer am Handy abgehakt wird, bekommt ein Häkchen. Läuft eine Frist ab,
 *    zieht der Worker neu — hier rattert dann nur diese Karte.
 * 4. **Fertig:** alle Gewinner stehen fest.
 *
 * Der Stand kommt jede Sekunde vom Worker; die Fristen laufen dort ab, nicht
 * hier — ein neu geladener Bildschirm zeigt sofort den richtigen Stand.
 */
export function GewinnspielPage({
  autostart = false,
}: {
  /** Gemeinsame Fernbedienung (`/buehne`): Der Klick kam schon dort — sofort starten. */
  autostart?: boolean
} = {}) {
  const [phase, setPhaseState] = useState<Phase>('bereit')
  const [stand, setStand] = useState<GewinnspielStand | null>(null)
  const [preise, setPreise] = useState<GewinnspielPreis[]>([])
  /** Preise, deren Karte gerade rattert: bis wann. */
  const [trommel, setTrommel] = useState<Record<string, number>>({})
  const [jetzt, setJetzt] = useState(() => Date.now())
  const [hinweis, setHinweis] = useState<string | null>(null)
  const reduceMotion = useReducedMotion()
  const hoehe = useFensterHoehe()

  const phaseRef = useRef<Phase>(phase)
  const setPhase = (p: Phase) => {
    phaseRef.current = p
    setPhaseState(p)
  }
  /** Welche Runde und Ziehungen je Preis schon gesehen sind; `null` = noch kein Stand. */
  const gesehen = useRef<{ runde: number; ziehung: Map<string, number> } | null>(null)
  const audio = useRef<HTMLAudioElement | null>(null)

  /** Spricht eine Ansage mit K4ias Stimme; ohne Ton (nicht konfiguriert, gestört) bleibt es still. */
  const sprechen = useCallback((art: GewinnspielAnsage) => {
    const el = audio.current
    if (!el) return
    el.src = gewinnspielSpeechUrl(art)
    el.play().catch((e: unknown) => {
      setHinweis(`Sprachausgabe nicht verfügbar: ${e instanceof Error ? e.message : String(e)}`)
    })
  }, [])

  const ladePreise = useCallback(() => {
    void fetchGewinnspielPreise()
      .then((r) => setPreise(r.preise))
      .catch(() => undefined)
  }, [])

  const verarbeiten = useCallback(
    (neu: GewinnspielStand) => {
      setStand(neu)
      const vorher = gesehen.current
      const ziehungen = new Map(neu.preise.map((p) => [p.id, p.ziehung]))
      gesehen.current = { runde: neu.runde, ziehung: ziehungen }
      const now = Date.now()

      if (neu.phase === 'wartet') {
        if (phaseRef.current !== 'wartet') {
          setTrommel({})
          setPhase('wartet')
          ladePreise()
        }
        return
      }

      // Erster Stand nach dem Aktivieren (oder Neuladen mitten in der Ziehung):
      // zeigen, was ist — ohne Trommel und ohne Ansage.
      if (!vorher || (phaseRef.current === 'wartet' && vorher.runde === neu.runde)) {
        setPhase(neu.phase === 'fertig' ? 'fertig' : 'ziehung')
        return
      }

      if (phaseRef.current === 'wartet') {
        // Die Ziehung beginnt: alle Karten rattern, K4ia sagt an.
        ladePreise()
        setPhase('ziehung')
        setTrommel(Object.fromEntries(neu.preise.map((p) => [p.id, now + TROMMEL_MS])))
        sprechen('start')
        return
      }

      // Einzelne Neuziehungen (Frist abgelaufen oder am Handy „Neu ziehen").
      const neuGezogen = neu.preise.filter(
        (p) => (vorher.ziehung.get(p.id) ?? p.ziehung) < p.ziehung && p.gewinner !== null,
      )
      if (neuGezogen.length > 0) {
        setTrommel((t) => ({
          ...t,
          ...Object.fromEntries(neuGezogen.map((p) => [p.id, now + TROMMEL_NEU_MS])),
        }))
        sprechen('neu')
      }

      if (neu.phase === 'fertig' && phaseRef.current !== 'fertig') {
        setPhase('fertig')
        sprechen('fertig')
      }
    },
    [ladePreise, sprechen],
  )

  // Melden und nachfragen in einem — wie beim Intro.
  useEffect(() => {
    if (phase === 'bereit') return
    let aktiv = true
    const takt = async () => {
      const p = phaseRef.current
      if (p === 'bereit') return
      try {
        const neu = await reportGewinnspielScreen(p)
        if (aktiv) verarbeiten(neu)
      } catch {
        // Netz kurz weg: beim nächsten Durchlauf wieder
      }
    }
    void takt()
    const id = setInterval(() => void takt(), POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
    // Einmal nach dem Aktivieren; alles Weitere liest takt() aus den Refs.
  }, [phase === 'bereit', verarbeiten])

  // Die Uhr für Countdown und Trommel.
  useEffect(() => {
    const id = setInterval(() => setJetzt(Date.now()), 250)
    return () => clearInterval(id)
  }, [])

  // Ein neuer Stand ist deployt: neu laden — nur, solange nichts läuft.
  useEffect(() => {
    const id = setInterval(() => {
      if (phaseRef.current !== 'wartet' && phaseRef.current !== 'bereit') return
      void neuerStandDeployt()
        .then((neu) => {
          if (neu) void neuenStandLaden()
        })
        .catch(() => undefined)
    }, STAND_PRUEFEN_MS)
    return () => clearInterval(id)
  }, [])

  // Auf der Bühne kam der Klick schon vorher; beim Aushängen (Wechsel zu
  // Intro oder Gespräch) die laufende Ansage beenden.
  useEffect(() => {
    if (autostart) aktivieren()
    return () => audio.current?.pause()
    // Einmal beim Einhängen.
  }, [])

  function aktivieren() {
    // Im Klick: Nur so gibt der Browser dem Audio-Element später den Ton frei.
    if (!audio.current) {
      const el = new Audio()
      el.preload = 'auto'
      el.onplaying = () => setHinweis(null)
      // Einmal still abspielen, im Klick: Safari und strenge Einstellungen
      // lassen ein Element sonst nur direkt nach einem Klick spielen — die
      // Ansagen kommen aber Minuten später, auf Befehl vom Handy.
      el.src = stilleWav()
      void el.play().catch(() => undefined)
      audio.current = el
    }
    void document.documentElement.requestFullscreen?.().catch(() => undefined)
    gesehen.current = null
    ladePreise()
    setPhase('wartet')
  }

  if (phase === 'bereit') {
    return (
      <button
        onClick={aktivieren}
        className="flex min-h-screen w-full flex-col items-center justify-center gap-8 bg-bg p-12 text-center"
      >
        <Avatar state="idle" size={220} showLabel={false} />
        <div>
          <h1 className="text-3xl font-semibold">Gewinnspiel-Bildschirm</h1>
          <p className="mt-3 text-lg text-text-muted">
            Einmal klicken, um den Ton freizugeben — die Ziehung startet, sobald am Handy „Ziehung
            starten“ getippt wird.
          </p>
        </div>
      </button>
    )
  }

  const hinweisZeile = hinweis && (
    <p className="fixed bottom-3 left-3 max-w-md text-xs text-text-muted" role="status">
      {hinweis}
    </p>
  )

  if (phase === 'wartet' || !stand) {
    const raster = boxenRaster(preise.length)
    return (
      <main className="flex h-dvh flex-col items-center gap-[2vh] overflow-hidden bg-bg px-[3vw] py-[3vh] text-center">
        <motion.h1
          className="text-[clamp(2rem,6vh,4rem)] leading-none"
          initial={reduceMotion ? false : { opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Gewinnspiel
        </motion.h1>
        <motion.div
          animate={reduceMotion ? { y: 0 } : { y: [0, -12, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Avatar state="idle" size={Math.round(hoehe * 0.2)} showLabel={false} />
        </motion.div>
        <p className="text-[clamp(1rem,3vh,2rem)] text-text-muted">
          Gleich geht's los — diese Preise gibt es heute:
        </p>
        <ul
          className="grid min-h-0 w-full max-w-[90vw] flex-1"
          style={rasterStil(raster)}
          data-testid="gewinnspiel-preise"
        >
          {preise.map((p) => (
            <li
              key={p.id}
              className="flex min-h-0 flex-col items-center justify-center gap-[1.2vh] overflow-hidden rounded-2xl border-2 border-border bg-bg-elevated p-[1.6vh]"
            >
              <Logo preis={p} />
              <p className="text-[clamp(1rem,2.6vh,1.6rem)] font-semibold leading-tight">
                {p.company}
              </p>
              {p.prize && (
                <p className="text-[clamp(0.8rem,2vh,1.2rem)] text-text-muted">{p.prize}</p>
              )}
              {p.winnersCount > 1 && (
                <p className="text-[clamp(0.75rem,1.8vh,1rem)] text-text-muted">
                  {p.winnersCount} Gewinner
                </p>
              )}
            </li>
          ))}
        </ul>
        {hinweisZeile}
      </main>
    )
  }

  const raster = boxenRaster(stand.preise.length)
  const alleDa = phase === 'fertig'
  return (
    <main className="flex h-dvh flex-col items-center gap-[2vh] overflow-hidden bg-bg px-[3vw] py-[2.5vh] text-center">
      <div className="flex items-center gap-[1.5vw]">
        <Avatar
          state={alleDa ? 'idle' : 'speaking'}
          size={Math.round(hoehe * 0.11)}
          showLabel={false}
        />
        <h1 className="text-[clamp(1.75rem,5.5vh,3.5rem)] leading-none">
          {alleDa ? 'Herzlichen Glückwunsch!' : 'Die Gewinner'}
        </h1>
      </div>
      {!alleDa && (
        <p className="text-[clamp(1rem,2.8vh,1.75rem)] text-text-muted">
          Steht dein Name hier? Dann melde dich beim Team — du hast zehn Minuten.
        </p>
      )}
      <ul
        className="grid min-h-0 w-full max-w-[92vw] flex-1"
        style={rasterStil(raster)}
        data-testid="gewinnspiel-karten"
      >
        {stand.preise.map((z) => (
          <li key={z.id} className="min-h-0">
            <Karte
              preis={preise.find((p) => p.id === z.preisId)}
              stand={z}
              jetzt={jetzt}
              trommelBis={trommel[z.id] ?? 0}
              reduceMotion={Boolean(reduceMotion)}
            />
          </li>
        ))}
      </ul>
      {hinweisZeile}
    </main>
  )
}

/**
 * Alle Karten auf einen Bildschirm, ohne Scrollen: ein Raster mit so vielen
 * Spalten und Zeilen, wie `boxenRaster` sagt, jede Zelle gleich groß und
 * nicht höher als ihr Anteil. Am 10.10. stand die zweite Reihe mit acht
 * Losen halb unter dem Rand des Fernsehers, mit Scrollbalken — der Bildschirm
 * ist 16:9, und die Karten hatten feste Schriftgrößen.
 */
function rasterStil(raster: { spalten: number; reihen: number }): CSSProperties {
  return {
    gridTemplateColumns: `repeat(${raster.spalten}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${raster.reihen}, minmax(0, 1fr))`,
    gap: '1.5vh 1.2vw',
  }
}

/**
 * Schriftgröße des Namens in Pixeln: die Wunschgröße aus der Bildhöhe
 * (wie `clamp(1.1rem, 4.8vh, 3.2rem)`), verkleinert, bis der Name in die
 * Karte passt — eine Zeile, nie ein Umbruch in die Frist. Monospace: ein
 * Zeichen ist etwa 0,62 em breit. Ohne gemessene Breite (noch nicht
 * gezeichnet, Tests) gilt die Wunschgröße.
 */
export function namensGroesse(zeichen: number, kartenBreite: number, fensterHoehe: number): number {
  const wunsch = Math.min(Math.max(17.6, fensterHoehe * 0.048), 51.2)
  if (kartenBreite <= 0 || zeichen <= 0) return wunsch
  // Innenabstand der Karte links und rechts (p-[1.6vh]) abziehen, etwas Luft lassen.
  const nutzbar = kartenBreite - 2 * fensterHoehe * 0.016 - 8
  const passt = nutzbar / (zeichen * 0.62)
  return Math.max(12, Math.min(wunsch, passt))
}

/** Die gemessene Breite eines Elements — folgt dem Fenster (ResizeObserver). */
function useBreite<T extends HTMLElement>(): { ref: (el: T | null) => void; breite: number } {
  const [breite, setBreite] = useState(0)
  const beobachter = useRef<ResizeObserver | null>(null)
  const ref = useCallback((el: T | null) => {
    beobachter.current?.disconnect()
    beobachter.current = null
    if (!el) return
    setBreite(el.clientWidth)
    if (typeof ResizeObserver === 'undefined') return
    beobachter.current = new ResizeObserver(() => setBreite(el.clientWidth))
    beobachter.current.observe(el)
  }, [])
  return { ref, breite }
}

/** Die Höhe des Fensters — für Größen, die nicht in CSS gehen (der Avatar nimmt Pixel). */
function useFensterHoehe(): number {
  const [hoehe, setHoehe] = useState(() =>
    typeof window === 'undefined' ? 1080 : window.innerHeight,
  )
  useEffect(() => {
    const lesen = () => setHoehe(window.innerHeight)
    window.addEventListener('resize', lesen)
    return () => window.removeEventListener('resize', lesen)
  }, [])
  return hoehe
}

function Logo({ preis }: { preis: GewinnspielPreis | undefined }) {
  return (
    <div className="flex h-[clamp(2.75rem,10vh,6rem)] w-full shrink-0 items-center justify-center rounded-xl bg-white px-3 py-1">
      {preis?.logoKey ? (
        <img
          src={partnerLogoPfad(preis.logoKey)}
          alt={preis.company}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="text-[clamp(0.9rem,2.2vh,1.25rem)] font-semibold text-neutral-800">
          {preis?.company ?? ''}
        </span>
      )}
    </div>
  )
}

/** Eine Karte je Preis: Logo, Firma, der gezogene Name, die Frist — oder das Häkchen. */
function Karte({
  preis,
  stand,
  jetzt,
  trommelBis,
  reduceMotion,
}: {
  preis: GewinnspielPreis | undefined
  stand: GewinnspielPreisStand
  jetzt: number
  trommelBis: number
  reduceMotion: boolean
}) {
  const rattert = jetzt < trommelBis && stand.bestaetigtAm === null
  const da = stand.bestaetigtAm !== null
  const niemand = stand.gewinner === null
  const name = rattert
    ? TROMMEL_NAMEN[Math.floor(jetzt / 90) % TROMMEL_NAMEN.length]!
    : (stand.gewinner ?? '')
  const rest = restzeit(stand.fristBis, jetzt)
  const knapp = !da && !rattert && !niemand && stand.fristBis - jetzt < 60_000
  const anzeige = niemand && !rattert ? '—' : `@${name}`
  const { ref: karte, breite } = useBreite<HTMLElement>()
  const hoehe = useFensterHoehe()

  return (
    <motion.article
      ref={karte}
      data-testid="gewinnspiel-karte"
      data-status={da ? 'da' : rattert ? 'trommel' : niemand ? 'niemand' : 'offen'}
      className={`flex h-full min-h-0 flex-col items-center justify-center gap-[1.2vh] overflow-hidden rounded-2xl border-2 p-[1.6vh] ${
        da
          ? 'border-[var(--color-status-active)] bg-bg-elevated shadow-[0_0_40px_var(--color-status-active)]'
          : 'border-border bg-bg-elevated'
      }`}
      animate={
        reduceMotion
          ? undefined
          : da
            ? { scale: [1, 1.04, 1] }
            : rattert
              ? { scale: [1, 1.02, 1] }
              : { scale: 1 }
      }
      transition={rattert ? { duration: 0.6, repeat: Infinity } : { duration: 0.6 }}
    >
      <Logo preis={preis} />
      <p className="text-[clamp(0.9rem,2.7vh,1.7rem)] font-semibold leading-tight">
        {preis?.company ?? ''}
        {preis && preis.winnersCount > 1 && (
          <span className="ml-2 rounded-full bg-surface-2 px-2 py-0.5 text-[0.7em] font-normal text-text-muted">
            {stand.los + 1} von {preis.winnersCount}
          </span>
        )}
      </p>
      {preis?.prize && (
        <p className="-mt-[0.6vh] text-[clamp(0.75rem,2.1vh,1.2rem)] text-text-muted">
          {preis.prize}
        </p>
      )}
      <p
        className={`min-h-[1.2em] whitespace-nowrap font-mono font-bold leading-tight ${
          rattert ? 'text-text-muted' : ''
        }`}
        // So groß wie möglich, aber auf einer Zeile: Ein langer Name wie
        // @modeecke_huchenfeld brach um und lief in die Frist (Foto, 10.10.).
        style={{ fontSize: `${namensGroesse(anzeige.length, breite, hoehe)}px` }}
      >
        {anzeige}
      </p>
      {da ? (
        <p className="inline-flex items-center gap-2 rounded-full bg-[var(--color-status-active)] px-[1.2em] py-[0.3em] text-[clamp(0.9rem,2.2vh,1.25rem)] font-semibold text-white">
          <span aria-hidden>✓</span> Gewinner ist da
        </p>
      ) : rattert ? (
        <p className="text-[clamp(0.9rem,2.5vh,1.5rem)] text-text-muted">K4ia zieht …</p>
      ) : niemand ? (
        <p className="text-[clamp(0.9rem,2.2vh,1.25rem)] text-text-muted">Niemand mehr übrig</p>
      ) : (
        <p
          className={`font-mono text-[clamp(1.25rem,4.8vh,3.2rem)] leading-none tabular-nums ${knapp ? 'text-status-error' : ''}`}
          aria-label={`Noch ${rest}`}
        >
          {rest}
        </p>
      )}
      {!da && !rattert && !niemand && stand.ziehung > 1 && (
        <p className="text-[clamp(0.7rem,1.6vh,0.9rem)] text-text-muted">
          {stand.ziehung}. Ziehung
        </p>
      )}
    </motion.article>
  )
}
